// =============================================================================
// Rolled — REORDERS page. A small, standalone screen a back-of-house staffer
// opens from a link (meadowhall.eatrolled.com/?view=reorders or
// mcr-kitchen.eatrolled.com/?view=reorders). They sign in with their staff PIN,
// see ONLY the open reorder requests they're responsible for at their site, and
// mark an item as reordered (re-confirming with their PIN). The mark writes the
// SAME 'ordered' lifecycle row the Command Centre writes, so it shows up in the
// CC's Open Reorders with no change to the admin app.
//
// Mounted by src/main.jsx as its own app when the URL carries ?view=reorders
// (chosen before the normal FOH/BOH mount). The site still comes from the host,
// so no site is needed in the URL.
//
// WHY THIS PAGE EXISTS: the kitchen app + CC already SHOW reorder flags, but
// only an ADMIN (6-digit PIN, stockOrdered) could mark one ordered. June and the
// kitchen had no way to close the loop themselves. This page gives the site's
// BOH owner a one-tap "Mark as reordered" that lands in the CC.
//
// SECURITY (deliberate, differs from the live FOH login):
//   * PIN is verified SERVER-SIDE via verifyPin() (Supabase auth-pin edge fn) —
//     we NEVER download the staff roster (?action=pinsForLogin), which is the
//     known unauthenticated PIN-leak endpoint.
//   * The mark action re-verifies the PIN (verifyPin) AND the Apps Script backend
//     re-checks it server-side against its role matrix (stockOrderedByBoh is
//     BOH/Admin only). Two independent server checks; nothing trusts the client.
// =============================================================================
import React, { useState, useEffect, useCallback, useRef } from "react";
import { getActiveSite } from "./config/sites.js";
import { verifyPin, ensureAnonSession } from "./lib/auth.js";
import { confirmedWrite, notifyWriteFailed, LOCAL_MARK_TTL_MS } from "./lib/confirmedWrite.js";
import { confirmByReRead } from "./lib/backendJson.js";
// Fast Supabase-backed stock reader (the estate read-flip, live on these hosts
// since 31 Aug — src/lib/stockReader.js). Reading the running list from Supabase
// is ~0.6s vs the Apps Script feed's ~10s. stockCatalog gives the active-item
// names for the deactivated-item filter (parity with the kitchen — a
// discontinued line with a lingering sticky flag must not resurface here).
import * as stockReader from "./lib/stockReader.js";
import * as stockCatalog from "./lib/stockCatalog.js";

const SITE = getActiveSite();
const BACKEND = SITE && SITE.backend;

// Build version — read off window (single source of truth in index.html). The
// auto-updater below polls the live HTML and surfaces a Refresh modal on drift,
// so a tablet left open on this page can't silently run a stale bundle.
const BUILD_VERSION = (typeof window !== "undefined" && window.BUILD_VERSION) || "unknown";

// Brand tokens. Copied from foh.jsx (where `B` is file-local, not exported).
const B = { nori: "#00392C", wasabi: "#D4FB6F", kewpie: "#FEFAF0", rice: "#FFFFFF", coral: "#FF7A5C", sunshine: "#F1C544", eucalyptus: "#6FAF8F" };

// Rolled wordmark (same base64 SVG the FOH/kitchen apps use).
const LOGO_B64 = "data:image/svg+xml;base64,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";
function RolledLogo({ height }) {
  height = height || 48;
  return React.createElement("img", { src: LOGO_B64, style: { height: height, width: "auto", display: "block" }, alt: "Rolled" });
}

// A product thumbnail for a reorder row, from the stock catalog (keyed by item
// name). Falls back to a neutral placeholder when the item has no image or the
// URL fails to load, so a broken/missing image never breaks the row layout.
function ReorderThumb({ src }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);   // a new src gets a fresh chance to load
  if (!src || broken) return <div style={ST.thumbPh} aria-hidden="true">🍱</div>;
  return <img src={src} alt="" loading="lazy" style={ST.thumb} onError={() => setBroken(true)} />;
}

// -----------------------------------------------------------------------------
// Auto-updater + version pill — copied from foh.jsx (both file-local there).
// A page a tablet leaves open all day MUST poll for new builds or it keeps
// running the cached bundle; MEMORY: every deploy bumps window.BUILD_VERSION.
// -----------------------------------------------------------------------------
function useAutoUpdater() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (cancelled) return;
      try {
        const url = window.location.pathname + "?_v=" + Date.now();
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok || cancelled) return;
        const html = await r.text();
        const m = html.match(/window\.BUILD_VERSION\s*=\s*"([^"]+)"/);
        if (m && m[1] !== BUILD_VERSION && !cancelled) setUpdateAvailable(true);
      } catch (e) {}
    };
    const firstCheck = setTimeout(check, 15000);
    const interval = setInterval(check, 120000);
    return () => { cancelled = true; clearTimeout(firstCheck); clearInterval(interval); };
  }, []);
  return updateAvailable;
}
function useVersionPillDOM(updateAvailable) {
  useEffect(() => {
    const pill = document.createElement("div");
    pill.id = "__rolled-version-pill";
    pill.style.cssText = "position:fixed;bottom:6px;right:10px;z-index:9998;font-family:monospace;font-size:10px;color:rgba(254,250,240,0.3);letter-spacing:0.02em;pointer-events:none;user-select:none";
    pill.textContent = "v" + BUILD_VERSION;
    document.body.appendChild(pill);
    return () => { try { document.body.removeChild(pill); } catch (e) {} };
  }, []);
  useEffect(() => {
    if (!updateAvailable) return;
    const backdrop = document.createElement("div");
    backdrop.id = "__rolled-update-modal";
    backdrop.style.cssText = "position:fixed;inset:0;background:rgba(0,20,15,0.85);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:24px;z-index:99999;animation:__rolledFade 0.25s ease-out";
    backdrop.innerHTML = '<style>@keyframes __rolledFade{from{opacity:0}to{opacity:1}}</style><div role="alertdialog" aria-modal="true" style="background:#00392C;color:#FEFAF0;padding:40px 36px;border-radius:20px;max-width:420px;width:100%;text-align:center;box-shadow:0 24px 64px rgba(0,0,0,0.45);border:1px solid rgba(212,251,111,0.15)"><div style="font-size:48px;margin-bottom:8px" aria-hidden="true">✨</div><div style="font-family:gelica,Georgia,serif;font-size:26px;font-weight:700;color:#D4FB6F;letter-spacing:-0.02em;margin-bottom:10px">New version ready</div><div style="font-size:15px;color:rgba(254,250,240,0.75);line-height:1.5;margin-bottom:28px">A newer build of this app is live. Tap refresh to load it.</div><button id="__rolled-update-refresh" style="background:#D4FB6F;color:#00392C;border:none;padding:18px 28px;border-radius:12px;font-family:Manrope,sans-serif;font-weight:800;font-size:17px;letter-spacing:0.02em;cursor:pointer;width:100%;box-shadow:0 4px 16px rgba(212,251,111,0.25);min-height:56px">↻ Refresh now</button></div>';
    document.body.appendChild(backdrop);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const btn = backdrop.querySelector("#__rolled-update-refresh");
    const reload = () => window.location.reload();
    btn.addEventListener("click", reload);
    const swallowKey = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); } };
    window.addEventListener("keydown", swallowKey, true);
    setTimeout(() => { try { btn.focus(); } catch (e) {} }, 50);
    return () => {
      btn.removeEventListener("click", reload);
      window.removeEventListener("keydown", swallowKey, true);
      document.body.style.overflow = prevOverflow;
      try { document.body.removeChild(backdrop); } catch (e) {}
    };
  }, [updateAvailable]);
}

// -----------------------------------------------------------------------------
// Scope — which site(s) a device's Reorders page covers, and who owns them.
// This mirrors the app's existing housed-stock grouping (boh-kitchen.jsx: the
// kitchen fans out to ["tc","kitchen"]; Meadowhall self-serves). Reorders are
// about the physical storeroom the tablet lives at, so the scope comes from the
// HOST's site (SITE.key), exactly as the FOH/kitchen apps resolve their site.
//   * Meadowhall tablet -> Meadowhall's reorders     (owner: June)
//   * Trafford tablet   -> Trafford's reorders       (owner: the Trafford kitchen — on the
//                          premises since 24 Sep 2026; the storeroom IS Trafford's)
//   * Kitchen tablet    -> Trafford + Kitchen reorders(owner: the kitchen) — the old
//                          Manchester host, kept until its closure PR
// Arndale is CLOSED (never a write target) so it is never in scope. Any other
// host (local dev with no ?site override) gets no page — this screen only runs
// where a BOH owner actually orders stock. Trafford's scope is ["tc"] only: the
// legacy "kitchen"-site rows are closed out before the reopen (merge scope A4),
// so nothing here ever re-opens a row under the old identity.
function reorderScopeFor(site) {
  const key = String((site && site.key) || "").toLowerCase();
  if (key === "meadowhall") return { sites: ["meadowhall"], owner: "June", multi: false };
  if (key === "tc") return { sites: ["tc"], owner: "the Trafford kitchen", multi: false };
  if (key === "mcr" || key === "kitchen") return { sites: ["tc", "kitchen"], owner: "the kitchen", multi: true };
  return null;
}

// A feed row is an OPEN reorder when it carries a running-low / out-of-stock
// flag and has NOT yet been marked ordered. This matches the Command Centre's
// Open Reorders reader (ManchesterReportWriter.handleGetOpenReorders), so this
// page and the CC agree on what's outstanding.
function isOpenReorder(r) {
  return !!r && (r.flag === "running_low" || r.flag === "out_of_stock") && r.ordered !== true;
}

const norm = (s) => String(s || "").trim().toLowerCase();
const roleTokens = (role) => String(role || "").split(",").map((s) => s.trim()).filter(Boolean);
// BOH inherits the right to mark ordered; Admin too. FOH-only staff cannot (they
// raise the request; BOH/kitchen places the order) — this mirrors the Apps
// Script role gate on stockOrderedByBoh, so the UI never offers an action the
// backend will refuse.
const canMarkOrdered = (role) => { const t = roleTokens(role); return t.indexOf("BOH") !== -1 || t.indexOf("Admin") !== -1; };
const rowKey = (r) => (r._site || "") + "::" + norm(r.item);
const enGbDate = () => { try { return new Date().toLocaleDateString("en-GB"); } catch (e) { return ""; } };

// A friendly label for a site key, for the per-row badge on the kitchen's
// multi-site queue.
const SITE_LABEL = { tc: "Trafford", kitchen: "Kitchen", meadowhall: "Meadowhall", arndale: "Arndale" };

// Race any promise against a hard timeout. fetchStockRequests reads Supabase
// fast, but its SHEET FALLBACK is a bare fetch with no client timeout — a hung
// sheet read would otherwise leave the page stuck on a spinner (and hold the
// in-flight guard). Resolves to `onTimeout` (default null → treated as an
// unreadable site) if `ms` elapses first.
function withTimeout(promise, ms, onTimeout = null) {
  let t;
  const timer = new Promise((res) => { t = setTimeout(() => res(onTimeout), ms); });
  return Promise.race([promise, timer]).finally(() => clearTimeout(t));
}

// Stale-while-revalidate cache of the last-good reorder list per scope, so an
// open paints the last-known items INSTANTLY while the slow feed refreshes in
// the background. The Apps Script feed is ~5s warm / up to ~30s cold, so without
// this a fresh open is a long blank spinner. Keyed by the scope's sites.
// The "v2" is a SCHEMA/SELECTION version — bump it whenever the set of rows this
// page shows changes shape, so an old cache written under different rules is
// discarded rather than painted. (v1 was the BOH-owner-filtered list; v2 shows
// all open reorders — carrying a v1 cache over would flash the old, wrongly
// filtered list on every open until the ~10s feed lands.)
const ROWS_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h
function rowsCacheKey() { return "rolled.reorders.rows.v2:" + (SCOPE ? SCOPE.sites.join(",") : "?"); }
function readRowsCache() {
  try {
    const raw = localStorage.getItem(rowsCacheKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.rows)) return null;
    if (Date.now() - parsed.ts >= ROWS_CACHE_TTL_MS) return null;
    return parsed.rows;
  } catch (e) { return null; }
}
function writeRowsCache(rows) {
  try { localStorage.setItem(rowsCacheKey(), JSON.stringify({ ts: Date.now(), rows })); } catch (e) {}
}

// Which reorder queue this deployment covers — computed ONCE. SITE is a module
// constant, so SCOPE is stable for the life of the page. Stability is load-
// bearing: a scope object rebuilt every render would give `load` a new identity
// each render and turn the 60s poll into a continuous refetch loop against the
// shared, quota-limited Apps Script backend.
const SCOPE = reorderScopeFor(SITE);

// One generic message for BOTH a wrong PIN and a valid-but-not-BOH PIN, so the
// page can't be used to tell which 4-digit codes are real staff PINs.
const PIN_DENIED = "PIN not recognised, or not cleared to mark reorders.";

// Portrait/landscape with a live listener, so rotating an idle kiosk reflows the
// numpad immediately rather than only after the next tap.
function useOrientation() {
  const [isPortrait, setIsPortrait] = useState(typeof window !== "undefined" ? window.innerHeight > window.innerWidth : true);
  useEffect(() => {
    const h = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener("resize", h);
    window.addEventListener("orientationchange", h);
    return () => { window.removeEventListener("resize", h); window.removeEventListener("orientationchange", h); };
  }, []);
  return isPortrait;
}

// =============================================================================
// APP
// =============================================================================
function ReordersApp() {
  const updateAvailable = useAutoUpdater();
  useVersionPillDOM(updateAvailable);
  const isPortrait = useOrientation();

  const [view, setView] = useState("login");          // 'login' | 'list'
  const [staff, setStaff] = useState(null);           // { name, role, homeSite }

  // Login PIN state
  const [pin, setPin] = useState("");
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState("");
  const [shake, setShake] = useState(false);

  // List state
  const [rows, setRows] = useState(null);             // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [orderedKeys, setOrderedKeys] = useState(() => new Set()); // rowKeys flipped to "Re-ordered"
  const [imgByName, setImgByName] = useState({});     // itemNameLower -> catalog image URL (row thumbnails)
  const loadingRef = useRef(false);                   // in-flight guard: never stack loads
  const markedRef = useRef(new Map());                // rowKey -> ts (TTL for the "Re-ordered" flip)
  const activeNamesRef = useRef(null);                // lowercase Set of active catalog names (null = fail open)
  const firstReadRef = useRef(true);                  // first read skips the slow CC-ordered overlay (fast paint)

  // Per-action PIN modal state
  const [pmOpen, setPmOpen] = useState(false);
  const [pmVal, setPmVal] = useState("");
  const [pmErr, setPmErr] = useState("");
  const [pmChecking, setPmChecking] = useState(false);
  const pmCb = useRef(null);                           // (staffName, staffPin) => Promise

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  const timeStr = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  // On mount: get the anon Supabase session so the first PIN check (verifyPin
  // via the auth-pin edge fn) is fast, and load the active-item names for the
  // deactivated-item filter. We deliberately do NOT fire warm-up FEED pings here
  // — the Apps Script /exec serialises concurrent cross-sheet reads, so extra
  // requests just queue in front of load()'s real fetches and make the list
  // SLOWER, not faster (the catalog read is its own fast Supabase RPC, not /exec).
  const activeNamesFromItems = (items) => {
    const n = (items || []).filter((c) => c && c.name && c.status !== "inactive").map((c) => String(c.name).toLowerCase());
    return n.length ? new Set(n) : null;
  };
  const imgMapFromItems = (items) => {
    const m = {};
    (items || []).forEach((c) => { if (c && c.name && c.imageUrl) m[norm(c.name)] = c.imageUrl; });
    return m;
  };
  const applyCatalog = (items) => {
    activeNamesRef.current = activeNamesFromItems(items);   // deactivated-item filter
    setImgByName(imgMapFromItems(items));                   // row thumbnails
  };
  useEffect(() => {
    ensureAnonSession().catch(() => {});
    // Seed active names + thumbnails from the catalog cache the FOH/kitchen apps
    // already keep on this origin (instant), then refresh. Until the names are
    // populated the deactivated filter fails OPEN — the safe direction (an extra
    // phantom item, never a hidden need) — for the brief first-cold-session only.
    // `cachedSeed` is the mount-time cached list (exactly the localStorage copy,
    // same idea as foh.jsx's mount-time `stockCatalog` state) — this effect runs
    // once, so it's what "the list already on screen" means for this page.
    let cachedSeed = [];
    try { const c = localStorage.getItem("stockCatalogCache"); if (c) { const a = JSON.parse(c); if (Array.isArray(a)) { cachedSeed = a; applyCatalog(a); } } } catch (e) {}
    stockCatalog.fetchStockCatalog({ sheetUrl: BACKEND + "?action=stockCatalog" })
      .then((d) => {
        // Lane CP: was `d && Array.isArray(d.items) && d.items.length`, its own
        // copy of the same rule every catalogue reader now shares via
        // stockCatalog.shouldReplaceCatalog — including the new "neither source
        // answered" shape (d.items === [], well-formed), which this already
        // refused (empty items) so nothing changes for that case either.
        if (stockCatalog.shouldReplaceCatalog(cachedSeed, d)) {
          applyCatalog(d.items);
          try { localStorage.setItem("stockCatalogCache", JSON.stringify(d.items)); } catch (e) {}
        }
      })
      .catch(() => {});
  }, []);

  // --- Load the responsibility-scoped open-reorder list ----------------------
  // Stable identity (no deps): SCOPE/BACKEND are module constants and everything
  // else is a ref or a setState fn, so the poll effect below sets up ONCE and the
  // 60s cadence is honoured (a per-render `load` would refetch on every render).
  const load = useCallback(async () => {
    if (!SCOPE) return;
    if (loadingRef.current) return;             // never stack concurrent reads
    loadingRef.current = true;
    setLoading(true);
    try {
      // Feeds: SEQUENTIAL, rendering each site as it lands. The first site in
      // every scope (tc, or meadowhall) reads from SUPABASE (~0.6s) via the
      // estate stock read-flip, so the real reorders paint almost instantly; the
      // kitchen storeroom (no Supabase site key → sheet fallback, ~5s, near-
      // always empty) is a non-blocking tail behind that first paint.
      // Every OPEN reorder at the site is shown, WHATEVER its owner: the reorder
      // email is routed per owner (June/kitchen/Adam/JJ), but it links here, so
      // the page must show the item the email was about — filtering to one owner
      // hid the others (e.g. Menu Flyers, owner JJ) and made the email a dead end.
      // CC-ordered overlay: 'ordered' marks placed from the Command Centre (and
      // from THIS page, via stockOrderedByBoh) are written to the SHEET only, so
      // the pure Supabase read is blind to them. applyCcOverlay folds them back
      // in from the sheet feed — this is what stops an already-ordered item
      // re-showing as an open reorder. But its COLD fetch awaits the slow feed,
      // so we skip it on the FIRST read (fast paint) and turn it on for the 60s
      // polls (warm/non-blocking after the first, and the list is already up);
      // a just-marked item is covered meanwhile by the local "Re-ordered" flip.
      // We do NOT shadow-write 'ordered' to Supabase from here: an independent
      // shadow that outlived a failed authoritative write would hide a genuine
      // open reorder (a stockout) — the overlay reads the authoritative sheet, so
      // a failed mark simply leaves the item visible.
      const useOverlay = !firstReadRef.current;
      const sortRows = (arr) => arr.slice().sort((a, b) =>
        String(b.flaggedFullTs || b.requestedFullTs || "").localeCompare(String(a.flaggedFullTs || a.requestedFullTs || "")));
      const seen = new Set();
      const acc = [];
      let anyUnreadable = false;
      for (const s of SCOPE.sites) {
        if (String(s).toLowerCase() === "arndale") continue;   // closed, never a write target
        const sheetUrl = BACKEND + "?action=getStockRequests&site=" + s;   // fallback + CC-overlay + kitchen source
        // activeItemNames hides deactivated items (null = fail open until the
        // catalog loads — safe direction). withTimeout bounds a hung sheet
        // fallback; it does not abort the underlying fetch (a stacked read during
        // a sustained /exec hang is bounded by the browser's per-origin cap and
        // self-heals — accepted over threading an AbortSignal through the reader).
        const d = await withTimeout(stockReader.fetchStockRequests(s, { sheetUrl, activeItemNames: activeNamesRef.current, ccOverlay: useOverlay }), 30000, null);
        if (!(d && Array.isArray(d.requests))) { anyUnreadable = true; continue; }
        d.requests.forEach((raw) => {
          const r = { ...raw, _site: s };
          if (!isOpenReorder(r)) return;
          const k = rowKey(r);
          if (seen.has(k)) return;
          seen.add(k);
          acc.push(r);
        });
        setRows(sortRows(acc));                   // incremental paint after each site
      }
      firstReadRef.current = false;               // subsequent polls fold in the CC-ordered overlay

      // Expire a stale "Re-ordered" flip: if a mark's write silently failed, let
      // the button become clickable again after the TTL.
      const nowMs = Date.now();
      const expired = [];
      for (const [k, ts] of markedRef.current) if (nowMs - ts >= LOCAL_MARK_TTL_MS) { markedRef.current.delete(k); expired.push(k); }
      if (expired.length) setOrderedKeys((s) => { const n = new Set(s); expired.forEach((k) => n.delete(k)); return n; });

      const finalRows = sortRows(acc);
      if (anyUnreadable && acc.length === 0) {
        // Nothing read at all — keep the last-known list (SWR) rather than a
        // false "nothing to reorder", and say so.
        setLoadErr("Couldn't reach the stock system — retrying.");
        setRows((prev) => (prev == null ? finalRows : prev));
      } else {
        setLoadErr(anyUnreadable ? "Part of the list didn't load — retrying." : "");
        setRows(finalRows);
        writeRowsCache(finalRows);                // stash the last-good list for an instant next open
      }
    } catch (e) {
      setLoadErr("Couldn't load the reorder list — tap Refresh to try again.");
      setRows((prev) => prev);                  // keep whatever was there; never force empty
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  // Poll while the list is open so a flag raised elsewhere appears within ~a
  // minute, matching the kitchen tablet's refresh cadence. `load` is stable, so
  // this effect sets up exactly once per entry to the list.
  useEffect(() => {
    if (view !== "list") return;
    // Stale-while-revalidate: paint the last-good list INSTANTLY, then refresh.
    const cached = readRowsCache();
    if (cached) setRows((prev) => (prev == null ? cached : prev));
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [view, load]);

  // --- Login -----------------------------------------------------------------
  const verifyLogin = useCallback(async (candidate) => {
    setChecking(true);
    setErr("");
    try {
      // NB: no siteId — verify_pin 500s on a non-UUID site id (the kitchen's
      // SITE.id is the string "kitchen"), and we only need identity + role here.
      const r = await verifyPin(candidate);
      if (r && r.ok && canMarkOrdered(r.role)) {
        setStaff({ name: r.staffName || "Staff", role: r.role || "", homeSite: r.homeSite || null });
        setView("list");
        setPin("");
        return;
      }
      // Wrong PIN AND valid-but-not-BOH share ONE generic message (no oracle);
      // only rate-limit + offline get their own (they don't reveal PIN validity).
      if (r && r.reason === "locked") setErr("Too many attempts — wait a minute, then try again.");
      else setErr((r && !r.ok && r.error) || PIN_DENIED);
      setShake(true);
      setTimeout(() => setShake(false), 450);
      setPin("");
    } catch (e) {
      setErr("Couldn't reach the login service. Check signal and try again.");
      setPin("");
    } finally {
      setChecking(false);
    }
  }, []);

  // Compute the next PIN and fire the server check OUTSIDE the state updater —
  // updaters must be pure (a side effect here double-fires under StrictMode and
  // double-counts against the shared login lock).
  const pushDigit = useCallback((d) => {
    if (checking || pin.length >= 4) return;
    const next = pin + d;
    setPin(next);
    if (next.length === 4) verifyLogin(next);
  }, [checking, pin, verifyLogin]);
  const delDigit = useCallback(() => { if (!checking) setPin((p) => p.slice(0, -1)); }, [checking]);

  const logout = useCallback(() => {
    setStaff(null); setRows(null); setView("login"); setErr(""); setPin("");
  }, []);

  // --- Per-action PIN modal --------------------------------------------------
  const requestPin = useCallback((cb) => {
    pmCb.current = cb;
    setPmVal(""); setPmErr(""); setPmChecking(false); setPmOpen(true);
  }, []);
  const closePin = useCallback(() => {
    setPmOpen(false); setPmVal(""); setPmErr(""); setPmChecking(false); pmCb.current = null;
  }, []);
  const submitPin = useCallback(async (candidate) => {
    setPmChecking(true); setPmErr("");
    try {
      const r = await verifyPin(candidate);   // re-verify server-side (no siteId — see verifyLogin)
      // Wrong PIN and valid-but-not-BOH share ONE generic message (no oracle).
      if (!(r && r.ok && canMarkOrdered(r.role))) {
        setPmErr(r && r.reason === "locked" ? "Too many attempts — wait a minute." : (r && !r.ok && r.error) || PIN_DENIED);
        setPmVal("");
        setPmChecking(false);
        return;
      }
      const cb = pmCb.current;
      closePin();
      if (cb) await cb(r.staffName || (staff && staff.name) || "Staff", candidate);
    } catch (e) {
      setPmErr("Couldn't reach the login service.");
      setPmVal("");
      setPmChecking(false);
    }
  }, [closePin, staff]);
  const pmDigit = useCallback((d) => {
    if (pmChecking || pmVal.length >= 4) return;
    const next = pmVal + d;
    setPmVal(next);
    if (next.length === 4) submitPin(next);
  }, [pmChecking, pmVal, submitPin]);
  const pmDel = useCallback(() => { if (!pmChecking) setPmVal((p) => p.slice(0, -1)); }, [pmChecking]);

  // --- Mark reordered --------------------------------------------------------
  // Writes the SAME 'ordered' lifecycle row the CC writes, via the BOH-gated
  // stockOrderedByBoh action. staffName + staffPin are appended by confirmedWrite
  // and re-validated server-side (role matrix). The row's button flips IN PLACE
  // to a static "Re-ordered" badge (the confirmation) and rolls back to a
  // clickable button with a plain error if the write didn't land.
  const doMark = useCallback((row) => (staffName, staffPin) => {
    const k = rowKey(row);
    // NB: we do NOT write the 'ordered' event to Supabase from here. The mark
    // lands on the AUTHORITATIVE sheet via stockOrderedByBoh below; the fast read
    // then drops the item once its 60s poll folds that sheet 'ordered' row back
    // in through the CC-ordered overlay (see load()). Shadowing to Supabase
    // independently would let a shadow outlive a FAILED authoritative write and
    // silently hide a still-open reorder — a stockout. Until the next poll the
    // local "Re-ordered" flip (orderedKeys, below) is the confirmation.
    return confirmedWrite({
      backend: BACKEND,
      action: "stockOrderedByBoh",
      payload: {
        site: row._site,
        item: row.item,
        type: "standard",
        date: enGbDate(),
        note: "Marked reordered from the Reorders page",
      },
      staffName,
      staffPin,
      onOptimistic: () => {
        // Flip the button to "Re-ordered" (non-clickable) straight away; stamp the
        // TTL so a silently-failed write eventually becomes clickable again.
        markedRef.current.set(k, Date.now());
        setOrderedKeys((s) => new Set(s).add(k));
      },
      onSuccess: () => {},                        // the flipped button IS the confirmation
      onRollback: (msg) => {
        markedRef.current.delete(k);              // write failed -> button clickable again
        setOrderedKeys((s) => { const n = new Set(s); n.delete(k); return n; });
        notifyWriteFailed(row.item + " didn't save — " + (msg || "try again."));
      },
      // Non-idempotent: re-read the truth on an UNCERTAIN transport outcome
      // rather than re-POST. Landed == the item now reads ordered:true.
      verify: () => confirmByReRead(
        BACKEND + "?action=getStockRequests&site=" + row._site,
        (d) => (d && Array.isArray(d.requests) ? d.requests : []).some((r) => norm(r.item) === norm(row.item) && r.ordered === true),
        { onUnreadable: true }
      ),
    });
  }, []);

  // --- Render: no scope (page not meant for this device) ---------------------
  if (!SCOPE) {
    return (
      <div style={{ ...ST.loginRoot }}>
        <div style={ST.strip}><div style={ST.stripDot} /><span style={ST.stripTxt}>{(SITE && SITE.name) || "Rolled"}</span></div>
        <div style={{ ...ST.loginBody, textAlign: "center", padding: "0 24px" }}>
          <RolledLogo height={44} />
          <div style={{ fontFamily: "gelica,serif", fontSize: "1.3rem", fontWeight: 700, color: B.wasabi, marginTop: 20 }}>Reorders</div>
          <div style={{ color: B.kewpie, opacity: 0.55, fontSize: "0.9rem", lineHeight: 1.5, marginTop: 10, maxWidth: 320 }}>
            This page runs on the Meadowhall and Trafford tablets, where back-of-house orders stock.
          </div>
        </div>
      </div>
    );
  }

  // --- Render: PIN modal (shared by list actions) ----------------------------
  const pinModal = pmOpen ? (
    <div style={ST.overlay}>
      <div style={{ ...ST.modal, display: "flex", flexDirection: "column", alignItems: "center", padding: isPortrait ? 24 : 16, maxWidth: isPortrait ? 380 : 340 }}>
        <div style={{ ...ST.modalTitle, textAlign: "center", fontSize: isPortrait ? "1.1rem" : "0.95rem" }}>Staff PIN</div>
        <div style={{ ...ST.modalDesc, textAlign: "center", marginBottom: isPortrait ? 18 : 8, fontSize: isPortrait ? "0.8rem" : "0.72rem" }}>Enter your 4-digit PIN to confirm the reorder</div>
        <div style={{ ...ST.dots, marginBottom: isPortrait ? 10 : 6, gap: isPortrait ? 20 : 14 }}>
          {[0, 1, 2, 3].map((i) => (<div key={i} style={{ ...ST.dot, ...(i < pmVal.length ? ST.dotFilled : ST.dotEmpty), width: isPortrait ? 20 : 16, height: isPortrait ? 20 : 16 }} />))}
        </div>
        <div style={{ color: pmChecking ? B.kewpie : B.coral, opacity: pmChecking ? 0.6 : 1, fontSize: "0.78rem", fontWeight: 600, height: 18, marginBottom: isPortrait ? 6 : 2, textAlign: "center" }}>{pmChecking ? "Checking…" : (pmErr || " ")}</div>
        <div style={{ ...ST.pad, gap: isPortrait ? 10 : 6, maxWidth: isPortrait ? 280 : 220 }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, "del"].map((k, i) => {
            if (k === null) return (<div key={i} />);
            return (<button key={i} style={{ ...ST.key, aspectRatio: isPortrait ? "1.35" : "1.8", fontSize: isPortrait ? "1.6rem" : "1.2rem", borderRadius: isPortrait ? 14 : 10 }} onClick={() => (k === "del" ? pmDel() : pmDigit(String(k)))} disabled={pmChecking}>{k === "del" ? "⌫" : k}</button>);
          })}
        </div>
        <button style={{ ...ST.modalCancel, marginTop: isPortrait ? 18 : 8, width: "100%", maxWidth: isPortrait ? 280 : 220, padding: isPortrait ? 12 : 8, fontSize: "0.8rem" }} onClick={closePin} disabled={pmChecking}>{pmChecking ? "Checking…" : "Cancel"}</button>
      </div>
    </div>
  ) : null;

  // --- Render: LOGIN ---------------------------------------------------------
  if (view === "login") {
    return (
      <div style={{ ...ST.loginRoot, height: "100dvh", overflow: "hidden" }}>
        <div style={ST.strip}><div style={ST.stripDot} /><span style={ST.stripTxt}>{SITE.name}</span></div>
        <div style={{ ...ST.loginBody, flex: 1, justifyContent: "center", paddingTop: 40 }}>
          <RolledLogo height={isPortrait ? 52 : 36} />
          <div style={{ fontFamily: "gelica,serif", fontSize: isPortrait ? "1.5rem" : "1.2rem", fontWeight: 700, color: B.wasabi, marginTop: 14 }}>Reorders</div>
          <div style={{ fontSize: "0.82rem", fontWeight: 600, color: B.kewpie, opacity: 0.4, marginTop: 6, marginBottom: isPortrait ? "2rem" : "1rem", letterSpacing: "0.06em" }}>{SITE.name}</div>
          <div style={{ ...ST.dots, ...(shake ? { animation: "shake 0.4s ease" } : {}) }}>
            {[0, 1, 2, 3].map((i) => (<div key={i} style={{ ...ST.dot, ...(i < pin.length ? ST.dotFilled : ST.dotEmpty) }} />))}
          </div>
          {checking ? <div style={ST.pinHint}>Checking…</div> : err ? <div style={ST.pinErr}>{err}</div> : <div style={ST.pinHint}>Enter your staff PIN</div>}
          <div style={{ ...ST.pad, gap: isPortrait ? 10 : 6, maxWidth: isPortrait ? 280 : 260 }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, "del"].map((k, i) => {
              if (k === null) return (<div key={i} />);
              return (<button key={i} style={{ ...ST.key, aspectRatio: isPortrait ? "1.35" : "1.6", fontSize: isPortrait ? "1.6rem" : "1.3rem" }} onClick={() => (k === "del" ? delDigit() : pushDigit(String(k)))} disabled={checking}>{k === "del" ? "⌫" : k}</button>);
            })}
          </div>
        </div>
        <div style={{ color: B.kewpie, opacity: 0.25, fontSize: "0.75rem", textAlign: "center", paddingBottom: isPortrait ? 20 : 12, paddingTop: 8, flexShrink: 0 }}>{dateStr} · {timeStr}</div>
      </div>
    );
  }

  // --- Render: LIST ----------------------------------------------------------
  return (
    <div style={ST.listRoot}>
      <div style={ST.topBar}>
        <div style={ST.topL}><RolledLogo height={24} /><span style={ST.hSite}>{SITE.name}</span></div>
        <div style={ST.topR}>
          <div style={ST.chip}><div style={ST.chipAv}>{(staff && staff.name && staff.name[0]) || "?"}</div><span style={ST.chipName}>{staff && staff.name}</span></div>
          <button style={ST.logoutBtn} onClick={logout}>Log out</button>
        </div>
      </div>

      <div style={ST.content}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", padding: "1.5rem 0 0.5rem" }}>
          <div>
            <div style={{ fontFamily: "gelica,serif", fontSize: "1.6rem", fontWeight: 700, color: B.kewpie, lineHeight: 1.15 }}>Open Reorders</div>
            <div style={{ fontSize: "0.85rem", color: B.kewpie, opacity: 0.5, marginTop: 4 }}>Flagged items waiting to be ordered — {SCOPE.owner}</div>
          </div>
          <button style={ST.refreshBtn} onClick={load} disabled={loading}>{loading ? "…" : "↻ Refresh"}</button>
        </div>

        {loadErr && <div style={{ ...ST.infoBanner, borderColor: "rgba(255,122,92,0.3)", background: "rgba(255,122,92,0.06)", color: B.coral }}>{loadErr}</div>}

        {/* Refreshing an already-painted (possibly stale) list. The Apps Script
            feed is slow (~10s), so without this the user stares at the last-known
            list — which right after an update can be the OLD one — with no sign
            it's still fetching, and assumes it's frozen / missing items. */}
        {loading && rows !== null && (
          <div style={ST.updatingStrip}><span style={ST.updatingDot} />Updating — checking the stock system…</div>
        )}

        {rows === null && loading && (
          <div style={ST.loadingBox}>
            <div style={ST.spinner} />
            <div style={{ marginTop: 16, fontSize: "0.82rem", color: B.kewpie, opacity: 0.5, textAlign: "center", maxWidth: 260 }}>
              Loading from the stock system…<br />the first open can take a few seconds.
            </div>
          </div>
        )}

        {rows !== null && rows.length === 0 && !loading && !loadErr && (
          <div style={ST.emptyState}>
            <div style={{ fontSize: "2rem", marginBottom: 8 }}>🍻</div>
            Nothing to reorder right now.<br />New flags appear here within a minute.
          </div>
        )}

        {rows && rows.length > 0 && rows.map((r) => {
          const oos = r.flag === "out_of_stock";
          const marked = orderedKeys.has(rowKey(r));
          return (
            <div key={rowKey(r)} style={ST.reRow}>
              <ReorderThumb src={imgByName[norm(r.item)]} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={ST.itemName}>{r.item}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                  <span style={{ ...ST.flagBadge, color: oos ? B.coral : B.sunshine, borderColor: oos ? "rgba(255,122,92,0.3)" : "rgba(241,197,68,0.3)", background: oos ? "rgba(255,122,92,0.08)" : "rgba(241,197,68,0.08)" }}>
                    {oos ? "❗ Out of stock" : "🪫 Running low"}
                  </span>
                  {SCOPE.multi && <span style={ST.siteBadge}>{SITE_LABEL[r._site] || r._site}</span>}
                  {r.flaggedBy && <span style={{ fontSize: "0.72rem", color: B.kewpie, opacity: 0.4 }}>flagged by {r.flaggedBy}</span>}
                </div>
              </div>
              {marked
                ? <span style={ST.reorderedBadge} role="status" aria-label={r.item + " re-ordered"}>Re-ordered</span>
                : <button style={ST.markBtn} onClick={() => requestPin(doMark(r))}>Mark as reordered</button>}
            </div>
          );
        })}
      </div>

      {pinModal}
    </div>
  );
}

// =============================================================================
// STYLES — the subset of foh.jsx's `S` this page uses (that `S` is file-local),
// plus a few list-specific rows. Colours/tokens from `B`. Animations (shake,
// viewIn, fadeUp, spin) are defined in index.html and shared by every screen.
// =============================================================================
const ST = {
  loginRoot: { minHeight: "100vh", background: B.nori, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "Manrope,sans-serif", position: "relative", animation: "viewIn 0.18s ease both" },
  strip: { position: "absolute", top: 0, left: 0, right: 0, background: "rgba(0,0,0,0.2)", padding: "10px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  stripDot: { width: 8, height: 8, borderRadius: "50%", background: B.wasabi },
  stripTxt: { fontWeight: 700, fontSize: "0.8rem", color: B.kewpie, letterSpacing: "0.08em", textTransform: "uppercase" },
  loginBody: { display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: 340 },
  dots: { display: "flex", gap: 20, marginBottom: 10 },
  dot: { width: 20, height: 20, borderRadius: "50%", border: "2.5px solid rgba(212,251,111,0.3)", borderColor: "rgba(212,251,111,0.3)", background: "transparent", boxShadow: "none", transition: "all 0.15s ease" },
  dotFilled: { borderColor: B.wasabi, background: B.wasabi, boxShadow: "0 0 14px rgba(212,251,111,0.35)" },
  dotEmpty: { borderColor: "rgba(212,251,111,0.3)", background: "transparent", boxShadow: "none" },
  pinErr: { color: B.coral, fontSize: "0.82rem", fontWeight: 600, height: 20, marginBottom: 8 },
  pinHint: { color: B.kewpie, opacity: 0.45, fontSize: "0.82rem", height: 20, marginBottom: 8 },
  pad: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, width: "100%", maxWidth: 280 },
  key: { aspectRatio: "1.35", borderRadius: 14, border: "none", background: "rgba(212,251,111,0.1)", color: B.wasabi, fontSize: "1.6rem", fontFamily: "Manrope,sans-serif", fontWeight: 700, cursor: "pointer", transition: "all 0.1s ease", display: "flex", alignItems: "center", justifyContent: "center" },

  // List shell
  listRoot: { minHeight: "100vh", background: B.nori, fontFamily: "Manrope,sans-serif", padding: "0 1.5rem 2rem", display: "flex", flexDirection: "column", animation: "viewIn 0.2s ease both" },
  topBar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: "1px solid rgba(212,251,111,0.1)" },
  topL: { display: "flex", alignItems: "center", gap: 14 },
  hSite: { background: "rgba(212,251,111,0.12)", border: "1px solid rgba(212,251,111,0.2)", color: B.wasabi, borderRadius: 999, padding: "4px 14px", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" },
  topR: { display: "flex", alignItems: "center", gap: 12 },
  chip: { display: "flex", alignItems: "center", gap: 8, background: "rgba(254,250,240,0.07)", borderRadius: 999, padding: "4px 14px 4px 4px" },
  chipAv: { width: 28, height: 28, borderRadius: "50%", background: B.wasabi, color: B.nori, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.75rem" },
  chipName: { fontSize: "0.82rem", fontWeight: 600, color: B.kewpie },
  logoutBtn: { background: "none", border: "1px solid rgba(254,250,240,0.15)", borderRadius: 999, padding: "5px 14px", fontSize: "0.75rem", fontWeight: 600, color: B.kewpie, opacity: 0.5, cursor: "pointer", fontFamily: "Manrope,sans-serif" },
  content: { width: "100%", maxWidth: 640, margin: "0 auto" },
  refreshBtn: { background: "none", border: "1px solid rgba(212,251,111,0.2)", borderRadius: 10, padding: "8px 14px", fontSize: "0.78rem", fontWeight: 700, color: B.wasabi, cursor: "pointer", fontFamily: "Manrope,sans-serif", whiteSpace: "nowrap" },
  infoBanner: { padding: "10px 14px", borderRadius: 10, border: "1px solid", margin: "10px 0", fontSize: "0.8rem", lineHeight: 1.5 },
  updatingStrip: { display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 10, border: "1px solid rgba(212,251,111,0.18)", background: "rgba(212,251,111,0.06)", color: B.wasabi, margin: "10px 0", fontSize: "0.78rem", fontWeight: 600 },
  updatingDot: { width: 12, height: 12, flexShrink: 0, border: "2px solid rgba(212,251,111,0.25)", borderTopColor: B.wasabi, borderRadius: "50%", animation: "spin 0.8s linear infinite" },

  // Reorder row
  reRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 14px", background: "rgba(254,250,240,0.03)", border: "1px solid rgba(212,251,111,0.08)", borderRadius: 12, marginBottom: 8, animation: "fadeUp 0.3s ease both" },
  thumb: { width: 48, height: 48, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: "rgba(254,250,240,0.05)", border: "1px solid rgba(212,251,111,0.1)" },
  thumbPh: { width: 48, height: 48, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem", background: "rgba(254,250,240,0.05)", border: "1px solid rgba(212,251,111,0.1)", opacity: 0.5 },
  itemName: { fontSize: "0.95rem", fontWeight: 600, color: B.kewpie, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  flagBadge: { fontSize: "0.7rem", fontWeight: 700, borderRadius: 999, padding: "2px 8px", border: "1px solid" },
  siteBadge: { fontSize: "0.68rem", fontWeight: 700, color: B.kewpie, opacity: 0.6, background: "rgba(212,251,111,0.08)", borderRadius: 999, padding: "2px 8px", textTransform: "uppercase", letterSpacing: "0.04em" },
  markBtn: { padding: "10px 16px", borderRadius: 10, border: "none", background: B.wasabi, color: B.nori, fontSize: "0.8rem", fontWeight: 800, cursor: "pointer", fontFamily: "Manrope,sans-serif", whiteSpace: "nowrap", flexShrink: 0 },
  // Confirmation state — same footprint as markBtn, but Nori fill with a Wasabi
  // outline + Wasabi text, and not clickable.
  reorderedBadge: { padding: "10px 16px", borderRadius: 10, border: "1.5px solid " + B.wasabi, background: B.nori, color: B.wasabi, fontSize: "0.8rem", fontWeight: 800, fontFamily: "Manrope,sans-serif", whiteSpace: "nowrap", flexShrink: 0, cursor: "default", display: "inline-flex", alignItems: "center", justifyContent: "center" },

  // Modal (mirrors foh.jsx PinModal)
  overlay: { position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.6)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, boxSizing: "border-box", overflowY: "auto" },
  modal: { background: B.nori, border: "1px solid rgba(212,251,111,0.2)", borderRadius: 18, padding: 20, maxWidth: 380, width: "calc(100% - 32px)", maxHeight: "90vh", overflowY: "auto" },
  modalTitle: { fontFamily: "gelica,serif", fontSize: "1.1rem", fontWeight: 700, color: B.wasabi, marginBottom: 4 },
  modalDesc: { fontSize: "0.8rem", color: B.kewpie, opacity: 0.5, marginBottom: 14 },
  modalCancel: { flex: 1, padding: 12, borderRadius: 10, border: "1px solid rgba(254,250,240,0.15)", background: "none", color: B.kewpie, opacity: 0.5, fontSize: "0.85rem", fontWeight: 600, cursor: "pointer", fontFamily: "Manrope,sans-serif" },

  // Feedback
  loadingBox: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px" },
  spinner: { width: 32, height: 32, border: "3px solid rgba(212,251,111,0.15)", borderTopColor: B.wasabi, borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  emptyState: { textAlign: "center", padding: "60px 20px", fontSize: "0.9rem", color: B.kewpie, opacity: 0.5, lineHeight: 1.6 },
};

export { ReordersApp };
